import { getStaffServiceIds, bookingStaff } from "@/lib/staff-associations";
import { absenceDisplay } from "@/lib/staff-absence-display";
import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, Calendar, User, Clock, FolderOpen, Filter, Maximize2, Minimize2, Plus, X, Trash2, CalendarIcon, UserPlus, Mail, Phone, History, Star, DollarSign, Coffee, CalendarX, AlertTriangle } from "lucide-react";
import { format, startOfWeek, addDays, isSameDay, addWeeks, subWeeks } from "date-fns";
import { it } from "date-fns/locale";

// Helper function to parse datetime strings as local time (not UTC)
// This prevents the one-hour timezone offset issue with parseISO
const parseLocalDateTime = (dateTimeString) => {
  if (!dateTimeString) return null;
  // Remove trailing Z if present (treat as local time, not UTC)
  const cleanStr = dateTimeString.replace(/Z$/, '');
  // Parse the ISO string components manually
  const match = cleanStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):?(\d{2})?/);
  if (!match) {
    // Fallback: try parsing as date only
    const dateMatch = cleanStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      return new Date(parseInt(dateMatch[1]), parseInt(dateMatch[2]) - 1, parseInt(dateMatch[3]));
    }
    return new Date(cleanStr);
  }
  const [, year, month, day, hours, minutes, seconds = '0'] = match;
  return new Date(
    parseInt(year),
    parseInt(month) - 1, // Month is 0-indexed
    parseInt(day),
    parseInt(hours),
    parseInt(minutes),
    parseInt(seconds)
  );
};
import axios from "axios";
import { toast } from "sonner";

// Helper to convert hex to rgba for backgrounds
const hexToRgba = (hex, alpha = 0.15) => {
  if (!hex || !hex.startsWith('#')) return `rgba(100, 116, 139, ${alpha})`; // default slate
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Helper to determine if color is light (for text color contrast)
const isLightColor = (hex) => {
  if (!hex || !hex.startsWith('#')) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

const CalendarView = ({ businessId, staff = [], bookings = [], calendars = [], categories = [], services = [], onBookingCreated, isAdmin = false, newBookingHighlight = null }) => {
  const { token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedStaffId, setSelectedStaffId] = useState("all");
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false); // Date picker dialog state
  
  // Zoom level: pixels per hour (80=compact, 120=default, 180=detailed)
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('calendarZoomLevel');
    return saved ? parseInt(saved) : 120;
  });
  const ZOOM_LEVELS = [80, 120, 180];
  const ZOOM_LABELS = ["Compatto", "Normale", "Dettagliato"];
  const pixelsPerHour = zoomLevel;
  
  // Google Calendar events state
  const [googleEvents, setGoogleEvents] = useState([]);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [showGoogleEvents, setShowGoogleEvents] = useState(true);
  
  // Staff blocks state (pause/riposi)
  const [staffBlocks, setStaffBlocks] = useState([]);
  
  // Persist viewMode in localStorage
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('calendarViewMode');
    return saved || "week";
  });
  
  // Save viewMode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('calendarViewMode', viewMode);
  }, [viewMode]);
  
  // Save zoom level to localStorage
  useEffect(() => {
    localStorage.setItem('calendarZoomLevel', String(zoomLevel));
  }, [zoomLevel]);
  
  // Determine API base path based on role
  const apiBasePath = isAdmin ? `${API}/admin/businesses/${businessId}` : `${API}/my-business`;
  
  // Fetch Google Calendar events when date changes
  useEffect(() => {
    const fetchGoogleEvents = async () => {
      if (!businessId || !token) return;
      
      try {
        // Calculate date range based on view mode
        const startDate = viewMode === "day" 
          ? format(currentDate, "yyyy-MM-dd")
          : format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
        const endDate = viewMode === "day"
          ? format(currentDate, "yyyy-MM-dd")
          : format(addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6), "yyyy-MM-dd");
        
        const response = await axios.get(
          `${API}/business/${businessId}/google-calendar/events`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { start_date: startDate, end_date: endDate }
          }
        );
        
        setGoogleEvents(response.data.events || []);
        setGoogleCalendarConnected(response.data.connected || false);
      } catch (error) {
        // Silently fail - Google Calendar may not be connected
        console.log("Google Calendar not available:", error.message);
        setGoogleEvents([]);
        setGoogleCalendarConnected(false);
      }
    };
    
    fetchGoogleEvents();
  }, [businessId, token, currentDate, viewMode]);
  
  // Fetch staff blocks (pause/riposi)
  useEffect(() => {
    const fetchStaffBlocks = async () => {
      if (!businessId || !token) return;
      
      try {
        const blocksApiPath = isAdmin ? `${API}/admin/businesses/${businessId}/staff-blocks` : `${API}/my-business/staff-blocks`;
        const response = await axios.get(blocksApiPath, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStaffBlocks(response.data || []);
      } catch (error) {
        console.log("Staff blocks not available:", error.message);
        setStaffBlocks([]);
      }
    };
    
    fetchStaffBlocks();
  }, [businessId, token, isAdmin, currentDate]);
  
  // Block edit/delete dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  
  // Manual booking dialog
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    service_ids: [],
    staff_id: "",
    notes: "",
    booking_time: "",
    send_email: false,
    // Staff availability block options
    is_break: false,
    is_day_off: false,
    break_end_time: ""
  });
  
  // Customer autocomplete state
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  
  // Close service dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (bookingForm._serviceDropdownOpen && !e.target.closest('[data-testid="service-search-input"]') && !e.target.closest('.max-h-60')) {
        setBookingForm(prev => ({...prev, _serviceDropdownOpen: false}));
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [bookingForm._serviceDropdownOpen]);
  
  // Fetch customer suggestions based on name input
  const searchCustomers = async (query) => {
    if (!query || query.length < 2) {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    setSearchingCustomers(true);
    try {
      const response = await axios.get(`${apiBasePath}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: query, limit: 5 }
      });
      setCustomerSuggestions(response.data || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error searching customers:", error);
      setCustomerSuggestions([]);
    } finally {
      setSearchingCustomers(false);
    }
  };
  
  // Handle customer selection from suggestions
  const selectCustomer = (customer) => {
    setBookingForm({
      ...bookingForm,
      customer_name: customer.name || "",
      customer_email: customer.email || "",
      customer_phone: customer.phone || ""
    });
    setIsExistingCustomer(true); // Mark as existing customer
    setShowSuggestions(false);
    setCustomerSuggestions([]);
  };
  
  // Add new customer to business
  const handleAddNewCustomer = async () => {
    if (!newCustomerData) return;
    
    try {
      await axios.post(`${apiBasePath}/customers`, {
        name: newCustomerData.name,
        email: newCustomerData.email,
        phone: newCustomerData.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cliente aggiunto alla lista!");
    } catch (error) {
      toast.error("Errore nell'aggiunta del cliente");
    } finally {
      setShowAddCustomerDialog(false);
      setNewCustomerData(null);
    }
  };
  
  // Debounce customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (bookingForm.customer_name && bookingDialogOpen) {
        searchCustomers(bookingForm.customer_name);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [bookingForm.customer_name, bookingDialogOpen]);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  
  // Calculate dynamic hours based on bookings and calendars - recalculate when data changes
  const hours = useMemo(() => {
    let minHour = 8; // default start
    let maxHour = 20; // default end (extended to 20:00)
    
    // Check calendar working hours
    if (calendars.length > 0) {
      calendars.forEach(cal => {
        const wh = cal.working_hours || {};
        Object.values(wh).forEach(dayHours => {
          if (dayHours.enabled && dayHours.start && dayHours.end) {
            const startH = parseInt(dayHours.start.split(':')[0]);
            const endH = parseInt(dayHours.end.split(':')[0]);
            if (startH < minHour) minHour = startH;
            if (endH > maxHour) maxHour = endH;
          }
        });
      });
    }
    
    // Check bookings to extend hours if needed
    bookings.forEach(booking => {
      if (booking.start_datetime) {
        const startDate = parseLocalDateTime(booking.start_datetime);
        const endDate = parseLocalDateTime(booking.end_datetime) || startDate;
        if (startDate && endDate) {
          const startH = startDate.getHours();
          const endH = endDate.getHours() + (endDate.getMinutes() > 0 ? 1 : 0);
          if (startH < minHour) minHour = startH;
          if (endH > maxHour) maxHour = endH + 1; // +1 to ensure the slot is visible
        }
      }
    });
    
    // Ensure reasonable bounds
    minHour = Math.max(0, minHour);
    maxHour = Math.min(24, maxHour);
    
    return Array.from({ length: maxHour - minHour }, (_, i) => minHour + i);
  }, [bookings, calendars]);

  // Booking edit state
  const [editBookingDialogOpen, setEditBookingDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editBookingForm, setEditBookingForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    service_id: "",
    staff_id: "",
    notes: "",
    status: "confirmed",
    start_datetime: "",
    start_time: ""
  });
  
  // Customer history state
  const [customerHistory, setCustomerHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Drag & Drop state
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const navigateWeek = (direction) => {
    setCurrentDate(direction === "next" ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
  };

  const navigateDay = (direction) => {
    setCurrentDate(direction === "next" ? addDays(currentDate, 1) : addDays(currentDate, -1));
  };

  const goToToday = () => setCurrentDate(new Date());

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  // Get the staff member who actually performs a specific service
  const getStaffForService = (serviceId) => {
    return staff.find(s => getStaffServiceIds(s).includes(serviceId));
  };
  
  // Get staff color for a booking based on primary staff
  const getBookingStaffColor = (booking) => {
    // 1. Direct staff assignment
    if (booking.staff_id) {
      const s = staff.find(st => st.staff_id === booking.staff_id);
      if (s?.color) return s.color;
    }
    // 2. Calendar-based staff
    const calendar = calendars.find(c => c.calendar_id === booking.calendar_id);
    if (calendar?.staff_id) {
      const s = staff.find(st => st.staff_id === calendar.staff_id);
      if (s?.color) return s.color;
    }
    // 3. Service-based staff
    const serviceIds = booking.service_ids || booking.services_details?.map(s => s.service_id) || [];
    if (serviceIds.length > 0) {
      const s = getStaffForService(serviceIds[0]);
      if (s?.color) return s.color;
    }
    return null;
  };
  
  // Get ALL staff members involved in a booking (based on services they perform)
  const getBookingStaffAssignments = (booking) => {
    const serviceIds = booking.service_ids?.length ? booking.service_ids : (booking.services || booking.services_details || []).map(s => s.service_id);
    const assignments = [];
    const seenStaff = new Set();
    
    // PRIORITY: Use the booking's explicit staff_id first (set during manual booking)
    if (booking.staff_id || calendars.find(c => c.calendar_id === booking.calendar_id)?.staff_id) {
      const bookingStaff = staff.find(s => s.staff_id === (booking.staff_id || calendars.find(c => c.calendar_id === booking.calendar_id)?.staff_id));
      if (bookingStaff) {
        assignments.push({
          service_id: serviceIds[0] || null,
          service_name: serviceIds.length > 0 ? (services.find(s => s.service_id === serviceIds[0])?.name || serviceIds[0]) : null,
          staff_id: bookingStaff.staff_id,
          staff_name: bookingStaff.name,
          staff_image: bookingStaff.image_url,
          staff_color: bookingStaff.color
        });
        return assignments;
      }
    }
    
    for (const sid of serviceIds) {
      const svc = services.find(s => s.service_id === sid);
      const assignedStaff = getStaffForService(sid);
      if (assignedStaff && !seenStaff.has(assignedStaff.staff_id)) {
        seenStaff.add(assignedStaff.staff_id);
        assignments.push({
          service_id: sid,
          service_name: svc?.name || sid,
          staff_id: assignedStaff.staff_id,
          staff_name: assignedStaff.name,
          staff_image: assignedStaff.image_url,
          staff_color: assignedStaff.color
        });
      }
    }
    
    if (assignments.length === 0 && booking.staff_id) {
      const fallbackStaff = staff.find(s => s.staff_id === booking.staff_id);
      if (fallbackStaff) {
        assignments.push({
          service_id: null,
          service_name: null,
          staff_id: fallbackStaff.staff_id,
          staff_name: fallbackStaff.name,
          staff_image: fallbackStaff.image_url,
          staff_color: fallbackStaff.color
        });
      }
    }
    
    return assignments;
  };

  // Check if a booking involves a specific staff member
  const bookingInvolvesStaff = (booking, staffMember) => {
    // Direct staff assignment
    if (booking.staff_id === staffMember.staff_id) return true;
    // Check via service-staff assignments
    const assignments = getBookingStaffAssignments(booking);
    return assignments.some(a => a.staff_id === staffMember.staff_id);
  };

  // Get bookings for staff view (single day) - sorted by time
  const getBookingsForStaffSlot = (staffMember, hour) => {
    return filteredBookings
      .filter(booking => {
        if (!booking.start_datetime) return false;
        const bookingDate = parseLocalDateTime(booking.start_datetime);
        if (!bookingDate || !isSameDay(bookingDate, currentDate)) return false;
        const bookingHour = bookingDate.getHours();
        // Check if booking involves this staff member
        if (!bookingInvolvesStaff(booking, staffMember)) {
          // Unassigned bookings go to first staff
          if (!booking.staff_id && staff.length > 0 && staffMember !== sortedStaff[0]) return false;
          if (booking.staff_id) return false;
        }
        return bookingHour === hour;
      })
      .sort((a, b) => {
        const dateA = parseLocalDateTime(a.start_datetime);
        const dateB = parseLocalDateTime(b.start_datetime);
        if (!dateA || !dateB) return 0;
        return dateA.getTime() - dateB.getTime();
      });
  };

  // Get bookings that START at a specific hour for a staff member
  const getBookingsStartingAtStaffSlot = (staffMember, hour) => {
    return filteredBookings
      .filter(booking => {
        if (!booking.start_datetime) return false;
        const bookingDate = parseLocalDateTime(booking.start_datetime);
        if (!bookingDate || !isSameDay(bookingDate, currentDate)) return false;
        const bookingHour = bookingDate.getHours();
        // Check if booking involves this staff member
        if (!bookingInvolvesStaff(booking, staffMember)) {
          // Unassigned bookings go to first staff
          if (!booking.staff_id && staff.length > 0 && staffMember !== sortedStaff[0]) return false;
          if (booking.staff_id) return false;
        }
        return bookingHour === hour;
      })
      .sort((a, b) => {
        const dateA = parseLocalDateTime(a.start_datetime);
        const dateB = parseLocalDateTime(b.start_datetime);
        if (!dateA || !dateB) return 0;
        return dateA.getTime() - dateB.getTime();
      });
  };

  // Sort staff by order field
  const sortedStaff = useMemo(() => {
    return [...staff].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }, [staff]);

  // Open booking dialog for a specific slot
  const openBookingDialog = (day, hour, staffId = null) => {
    setSelectedSlot({ day, hour });
    // Format hour as HH:00 for the time input
    const formattedTime = `${String(hour).padStart(2, '0')}:00`;
    setBookingForm({
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      service_ids: [],
      staff_id: staffId || staff[0]?.staff_id || "",
      notes: "",
      booking_time: formattedTime,
      send_email: false,
      is_break: false,
      is_day_off: false,
      break_end_time: ""
    });
    setIsExistingCustomer(false);
    setBookingDialogOpen(true);
  };
  
  // State for new customer dialog
  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState(null);
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);

  // Create manual booking
  const handleCreateBooking = async (e) => {
    e.preventDefault();
    
    // If it's a break or day off, handle differently
    if (bookingForm.is_break || bookingForm.is_day_off) {
      try {
        const startDateTime = new Date(selectedSlot.day);
        let endDateTime = new Date(selectedSlot.day);
        
        if (bookingForm.is_day_off) {
          // Day off: block entire day (8:00 - 20:00)
          startDateTime.setHours(0, 0, 0, 0);
          endDateTime.setHours(23, 59, 0, 0);
        } else if (bookingForm.is_break) {
          // Break: use booking_time as start and break_end_time as end
          if (bookingForm.booking_time) {
            const [hours, minutes] = bookingForm.booking_time.split(':').map(Number);
            startDateTime.setHours(hours, minutes, 0, 0);
          } else {
            startDateTime.setHours(selectedSlot.hour, 0, 0, 0);
          }
          
          if (bookingForm.break_end_time) {
            const [endHours, endMinutes] = bookingForm.break_end_time.split(':').map(Number);
            endDateTime.setHours(endHours, endMinutes, 0, 0);
          } else {
            endDateTime = new Date(startDateTime);
            endDateTime.setHours(startDateTime.getHours() + 1);
          }
        }
        
        const blockData = {
          business_id: businessId,
          staff_id: bookingForm.staff_id || selectedSlot.staffId,
          start_datetime: format(startDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
          end_datetime: format(endDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
          block_type: bookingForm.is_day_off ? "day_off" : "break",
          notes: bookingForm.notes || (bookingForm.is_day_off ? "Riposo" : "Pausa")
        };
        
        await axios.post(`${apiBasePath}/staff-blocks`, blockData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(bookingForm.is_day_off ? "Riposo impostato!" : "Pausa impostata!");
        setBookingDialogOpen(false);
        setBookingForm({
          customer_name: "", customer_email: "", customer_phone: "",
          service_ids: [], staff_id: "", notes: "", booking_time: "",
          send_email: false, is_break: false, is_day_off: false, break_end_time: ""
        });
        if (onBookingCreated) onBookingCreated();
        return;
      } catch (error) {
        toast.error(error.response?.data?.detail || "Errore nel salvataggio");
        return;
      }
    }
    
    // Normal booking flow
    if (!selectedSlot || bookingForm.service_ids.length === 0) {
      toast.error("Seleziona almeno un servizio");
      return;
    }
    if (!bookingForm.staff_id) {
      toast.error("Seleziona un operatore");
      return;
    }
    
    try {
      const selectedServices = services.filter(s => bookingForm.service_ids.includes(s.service_id));
      const totalDuration = selectedServices.reduce((sum, s) => sum + (s.duration_minutes || 30), 0);
      
      // Check if any selected service is multi-session
      const multiSessionSvc = selectedServices.find(s => s.session_type === 'multi' && (s.num_sessions || 1) > 1);
      
      // Build base session date
      const startDT = new Date(selectedSlot.day);
      if (bookingForm.booking_time) {
        const [h, m] = bookingForm.booking_time.split(':').map(Number);
        startDT.setHours(h, m, 0, 0);
      } else {
        startDT.setHours(selectedSlot.hour, 0, 0, 0);
      }
      
      // Staff is always the one explicitly selected in the form
      const selectedStaffId = bookingForm.staff_id;
      
      // Build session dates
      const sessionDates = [];
      if (multiSessionSvc && bookingForm.sessionDates?.length > 0) {
        bookingForm.sessionDates.forEach(sd => sessionDates.push(sd));
      } else {
        sessionDates.push({ 
          date: format(startDT, "yyyy-MM-dd"), 
          time: format(startDT, "HH:mm") 
        });
      }
      
      const sessionGroupId = multiSessionSvc && sessionDates.length > 1 ? `sg_${Date.now().toString(36)}` : null;
      let totalBookingsCreated = 0;
      
      for (let i = 0; i < sessionDates.length; i++) {
        const sd = sessionDates[i];
        const startDateTime = new Date(`${sd.date}T${sd.time}:00`);
        const endDateTime = new Date(startDateTime);
        endDateTime.setMinutes(endDateTime.getMinutes() + totalDuration);
        
        const bookingData = {
          business_id: businessId,
          customer_name: bookingForm.customer_name,
          customer_email: bookingForm.customer_email,
          customer_phone: bookingForm.customer_phone,
          service_ids: bookingForm.service_ids,
          staff_id: selectedStaffId,
          start_datetime: format(startDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
          end_datetime: format(endDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
          notes: bookingForm.notes + (sessionGroupId ? ` [Seduta ${i + 1}/${sessionDates.length}]` : ""),
          send_email: bookingForm.send_email && totalBookingsCreated === 0,
          status: "confirmed",
          payment_status: "not_required",
          session_group_id: sessionGroupId,
          session_number: i + 1,
          total_sessions: sessionDates.length
        };
        
        await axios.post(`${apiBasePath}/bookings`, bookingData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        totalBookingsCreated++;
      }
      
      if (totalBookingsCreated > 1) {
        toast.success(`${totalBookingsCreated} sedute create con successo!`);
      } else {
        toast.success("Prenotazione creata!");
      }
      setBookingDialogOpen(false);
      
      // If this is a new customer (not selected from autocomplete), ask to add to customers list
      if (!isExistingCustomer && bookingForm.customer_name && (bookingForm.customer_email || bookingForm.customer_phone)) {
        setNewCustomerData({
          name: bookingForm.customer_name,
          email: bookingForm.customer_email,
          phone: bookingForm.customer_phone
        });
        setShowAddCustomerDialog(true);
      }
      
      if (onBookingCreated) onBookingCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    }
  };

  // Open edit booking dialog
  const openEditBooking = (booking) => {
    setSelectedBooking(booking);
    const startDate = parseLocalDateTime(booking.start_datetime) || new Date();
    setEditBookingForm({
      customer_name: booking.customer_name || "",
      customer_email: booking.customer_email || "",
      customer_phone: booking.customer_phone || "",
      service_id: booking.services?.[0]?.service_id || "",
      staff_id: booking.staff_id || "",
      notes: booking.notes || "",
      status: booking.status || "confirmed",
      start_datetime: format(startDate, "yyyy-MM-dd"),
      start_time: format(startDate, "HH:mm")
    });
    setCustomerHistory(null);
    setShowHistory(false);
    setEditBookingDialogOpen(true);
    
    // Load customer history if email exists
    if (booking.customer_email) {
      loadCustomerHistory(booking.customer_email);
    }
  };

  // Open block for editing/deleting
  const openBlockDialog = (block) => {
    if (block.is_absence) { toast.info("Gestisci questa assenza in Orari → Ferie e assenze operatori."); return; }
    setSelectedBlock(block);
    setBlockDialogOpen(true);
  };

  // Delete a staff block
  const handleDeleteBlock = async () => {
    if (!selectedBlock) return;
    
    try {
      const deleteUrl = isAdmin 
        ? `${API}/admin/businesses/${businessId}/staff-blocks/${selectedBlock.block_id}`
        : `${API}/my-business/staff-blocks/${selectedBlock.block_id}`;
      
      await axios.delete(deleteUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(selectedBlock.block_type === "day_off" ? "Riposo eliminato!" : "Pausa eliminata!");
      setBlockDialogOpen(false);
      setSelectedBlock(null);
      
      // Refresh blocks
      setStaffBlocks(prev => prev.filter(b => b.block_id !== selectedBlock.block_id));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // Move a staff block (drag & drop)
  const handleBlockDrop = async (block, newDay, newHour) => {
    if (!block) return;
    
    try {
      // Calculate duration
      const oldStart = parseLocalDateTime(block.start_datetime);
      const oldEnd = parseLocalDateTime(block.end_datetime);
      const durationMs = oldEnd - oldStart;
      
      // Create new start/end times
      const newStart = new Date(newDay);
      newStart.setHours(newHour, 0, 0, 0);
      const newEnd = new Date(newStart.getTime() + durationMs);
      
      // Check if moving to past
      if (newStart < new Date()) {
        toast.error("Non puoi spostare un blocco nel passato");
        return;
      }
      
      const updateUrl = isAdmin 
        ? `${API}/admin/businesses/${businessId}/staff-blocks/${block.block_id}`
        : `${API}/my-business/staff-blocks/${block.block_id}`;
      
      await axios.put(updateUrl, {
        start_datetime: format(newStart, "yyyy-MM-dd'T'HH:mm:ss"),
        end_datetime: format(newEnd, "yyyy-MM-dd'T'HH:mm:ss")
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Blocco spostato!");
      
      // Update local state
      setStaffBlocks(prev => prev.map(b => 
        b.block_id === block.block_id 
          ? { ...b, start_datetime: format(newStart, "yyyy-MM-dd'T'HH:mm:ss"), end_datetime: format(newEnd, "yyyy-MM-dd'T'HH:mm:ss") }
          : b
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nello spostamento");
    }
  };

  // Update booking
  const handleUpdateBooking = async (e) => {
    e.preventDefault();
    if (!selectedBooking) return;
    
    try {
      const selectedService = services.find(s => s.service_id === editBookingForm.service_id);
      const durationMinutes = selectedService?.duration_minutes || 30;
      
      // Create datetime string directly without timezone conversion
      const startDateTimeStr = `${editBookingForm.start_datetime}T${editBookingForm.start_time}:00`;
      
      // Calculate end time
      const [hours, minutes] = editBookingForm.start_time.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + durationMinutes;
      const endHours = Math.floor(totalMinutes / 60) % 24;
      const endMinutes = totalMinutes % 60;
      const endTimeStr = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:00`;
      const endDateTimeStr = `${editBookingForm.start_datetime}T${endTimeStr}`;
      
      const updateData = {
        customer_name: editBookingForm.customer_name,
        customer_email: editBookingForm.customer_email,
        customer_phone: editBookingForm.customer_phone,
        service_ids: editBookingForm.service_id ? [editBookingForm.service_id] : [],
        staff_id: editBookingForm.staff_id,
        notes: editBookingForm.notes,
        status: editBookingForm.status,
        start_datetime: startDateTimeStr,
        end_datetime: endDateTimeStr
      };
      
      await axios.put(`${apiBasePath}/bookings/${selectedBooking.booking_id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Prenotazione aggiornata!");
      setEditBookingDialogOpen(false);
      setSelectedBooking(null);
      if (onBookingCreated) onBookingCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  // Delete booking
  const handleDeleteBooking = async (bookingId) => {
    if (!confirm("Sei sicuro di voler eliminare questa prenotazione?")) return;
    
    try {
      await axios.delete(`${apiBasePath}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Prenotazione eliminata!");
      setEditBookingDialogOpen(false);
      setSelectedBooking(null);
      if (onBookingCreated) onBookingCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // Load customer history
  const loadCustomerHistory = async (email) => {
    if (!email) return;
    setLoadingHistory(true);
    try {
      const endpoint = isAdmin 
        ? `${API}/admin/businesses/${businessId}/customer-history?email=${encodeURIComponent(email)}`
        : `${API}/my-business/customer-history?email=${encodeURIComponent(email)}`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerHistory(response.data);
    } catch (error) {
      console.error("Error loading customer history:", error);
      setCustomerHistory(null);
    }
    setLoadingHistory(false);
  };

  // Check if customer is new (has only 1 booking - the current one)
  const isNewCustomer = (booking) => {
    if (!booking?.customer_email) return false;
    const customerEmail = booking.customer_email.toLowerCase();
    const customerBookings = bookings.filter(b => 
      b.customer_email?.toLowerCase() === customerEmail
    );
    return customerBookings.length <= 1;
  };

  // Drag & Drop handlers
  const handleDragStart = (e, booking) => {
    setDraggedBooking(booking);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', booking.booking_id);
    // Add visual feedback
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedBooking(null);
    setDragOverSlot(null);
  };

  const handleDragOver = (e, day, hour, staffId = null) => {
    e.preventDefault();
    
    // Check if target slot is in the past
    const targetDateTime = new Date(day);
    targetDateTime.setHours(hour, 0, 0, 0);
    const now = new Date();
    
    if (targetDateTime < now) {
      e.dataTransfer.dropEffect = 'none';
      setDragOverSlot(null);
      return;
    }
    
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot({ day: day.toISOString(), hour, staffId });
  };

  const handleDragLeave = (e) => {
    setDragOverSlot(null);
  };

  const handleDrop = async (e, day, hour, staffId = null) => {
    e.preventDefault();
    setDragOverSlot(null);
    
    // Check if this is a block drag
    const isBlock = e.dataTransfer.getData("isBlock") === "true";
    const blockId = e.dataTransfer.getData("blockId");
    
    if (isBlock && blockId) {
      // Handle block drop
      const block = staffBlocks.find(b => b.block_id === blockId);
      if (block) {
        await handleBlockDrop(block, day, hour);
      }
      return;
    }
    
    // Handle booking drop
    if (!draggedBooking) return;
    
    try {
      // Calculate new start datetime
      const newStartDateTime = new Date(day);
      newStartDateTime.setHours(hour, 0, 0, 0);
      
      // Validate: cannot move to past date/time
      const now = new Date();
      if (newStartDateTime < now) {
        toast.error("Non puoi spostare un appuntamento nel passato");
        setDraggedBooking(null);
        return;
      }
      
      // Calculate duration from original booking
      const oldStart = parseLocalDateTime(draggedBooking.start_datetime);
      const oldEnd = parseLocalDateTime(draggedBooking.end_datetime);
      const durationMs = oldEnd - oldStart;
      
      // Calculate new end datetime
      const newEndDateTime = new Date(newStartDateTime.getTime() + durationMs);
      
      const moveData = {
        start_datetime: format(newStartDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
        end_datetime: format(newEndDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
        staff_id: staffId
      };
      
      const endpoint = isAdmin 
        ? `${API}/admin/businesses/${businessId}/bookings/${draggedBooking.booking_id}/move`
        : `${API}/my-business/bookings/${draggedBooking.booking_id}/move`;
      
      await axios.put(endpoint, moveData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Appuntamento spostato!");
      if (onBookingCreated) onBookingCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nello spostamento");
    }
    
    setDraggedBooking(null);
  };

  // Mappa categoria -> colore (usa il colore HEX della categoria)
  const getCategoryStyle = (categoryId) => {
    if (!categoryId) return { bgColor: 'rgba(100, 116, 139, 0.15)', borderColor: 'rgba(100, 116, 139, 0.4)', textColor: '#475569' };
    const cat = categories.find(c => c.category_id === categoryId);
    if (!cat?.color) return { bgColor: 'rgba(100, 116, 139, 0.15)', borderColor: 'rgba(100, 116, 139, 0.4)', textColor: '#475569' };
    return {
      bgColor: hexToRgba(cat.color, 0.15),
      borderColor: hexToRgba(cat.color, 0.5),
      textColor: cat.color
    };
  };

  // Ottieni categoria dal servizio della prenotazione
  const getBookingCategory = (booking) => {
    if (!booking.services_details || booking.services_details.length === 0) return null;
    const firstServiceId = booking.service_ids?.[0] || booking.services_details[0]?.service_id;
    const service = services.find(s => s.service_id === firstServiceId);
    if (service?.category_id) {
      return categories.find(c => c.category_id === service.category_id);
    }
    return null;
  };

  // Filtra prenotazioni
  const filteredBookings = bookings.filter(booking => {
    // Filtro staff - check if booking involves the selected staff member
    if (selectedStaffId !== "all") {
      const selectedStaffMember = staff.find(s => s.staff_id === selectedStaffId);
      if (selectedStaffMember) {
        // Use the multi-staff aware check
        if (!bookingInvolvesStaff(booking, selectedStaffMember)) return false;
      } else {
        // Fallback: check booking.staff_id or calendar.staff_id
        const calendar = calendars.find(c => c.calendar_id === booking.calendar_id);
        if (booking.staff_id !== selectedStaffId && calendar?.staff_id !== selectedStaffId) return false;
      }
    }
    
    // Filtro categoria
    if (selectedCategoryId !== "all") {
      const category = getBookingCategory(booking);
      if (category?.category_id !== selectedCategoryId) return false;
    }
    
    return true;
  });

  // Get bookings for a specific day and hour - sorted by time
  const getBookingsForSlot = (day, hour) => {
    return filteredBookings
      .filter(booking => {
        if (!booking.start_datetime) return false;
        const bookingDate = parseLocalDateTime(booking.start_datetime);
        if (!bookingDate) return false;
        const bookingHour = bookingDate.getHours();
        return isSameDay(bookingDate, day) && bookingHour === hour;
      })
      .sort((a, b) => {
        const dateA = parseLocalDateTime(a.start_datetime);
        const dateB = parseLocalDateTime(b.start_datetime);
        if (!dateA || !dateB) return 0;
        return dateA.getTime() - dateB.getTime();
      });
  };

  // Get all bookings for a specific day
  const getBookingsForDay = (day) => {
    return filteredBookings.filter(booking => {
      if (!booking.start_datetime) return false;
      const bookingDate = parseLocalDateTime(booking.start_datetime);
      return bookingDate && isSameDay(bookingDate, day);
    });
  };

  // Get Google Calendar events for a specific day and hour
  const getGoogleEventsForSlot = (day, hour) => {
    if (!showGoogleEvents || !googleEvents.length) return [];
    
    return googleEvents.filter(event => {
      if (!event.start_datetime) return false;
      const eventDate = parseLocalDateTime(event.start_datetime);
      if (!eventDate) return false;
      const eventHour = eventDate.getHours();
      return isSameDay(eventDate, day) && eventHour === hour;
    });
  };

  // Get Google Calendar events for a specific day
  const getGoogleEventsForDay = (day) => {
    if (!showGoogleEvents || !googleEvents.length) return [];
    
    return googleEvents.filter(event => {
      if (!event.start_datetime) return false;
      const eventDate = parseLocalDateTime(event.start_datetime);
      return eventDate && isSameDay(eventDate, day);
    });
  };

  // Get staff blocks (pause/riposo) that START at a specific hour
  const getStaffBlocksStartingAtSlot = (day, hour, staffId = null) => {
    return staffBlocks.map(block => absenceDisplay(block, day, hours[0], hours[hours.length - 1])).filter(block => {
      if (!block) return false;
      if (!block.start_datetime) return false;
      const blockDate = block.display_start || parseLocalDateTime(block.start_datetime);
      if (!blockDate) return false;
      if (!isSameDay(blockDate, day)) return false;
      if (blockDate.getHours() !== hour) return false;
      if (staffId && block.staff_id !== staffId) return false;
      return true;
    });
  };

  // Get staff blocks for a specific staff member on a specific day
  const getStaffBlocksForStaffDay = (staffId, day) => {
    return staffBlocks.filter(block => {
      if (!block.start_datetime) return false;
      if (block.staff_id !== staffId) return false;
      const blockDate = parseLocalDateTime(block.start_datetime);
      return blockDate && isSameDay(blockDate, day);
    });
  };

  // Calculate block duration in hours
  const getBlockDurationHours = (block) => {
    if (!block.start_datetime || !block.end_datetime) return 1;
    const start = block.display_start || parseLocalDateTime(block.start_datetime);
    const end = block.display_end || parseLocalDateTime(block.end_datetime);
    if (!start || !end) return 1;
    return Math.max((end - start) / (1000 * 60 * 60), 0.5);
  };

  // Get bookings that START at a specific hour (for positioning in grid)
  const getBookingsStartingAtSlot = (day, hour) => {
    return filteredBookings
      .filter(booking => {
        if (!booking.start_datetime) return false;
        const bookingDate = parseLocalDateTime(booking.start_datetime);
        if (!bookingDate) return false;
        return isSameDay(bookingDate, day) && bookingDate.getHours() === hour;
      })
      .sort((a, b) => {
        const dateA = parseLocalDateTime(a.start_datetime);
        const dateB = parseLocalDateTime(b.start_datetime);
        if (!dateA || !dateB) return 0;
        return dateA.getTime() - dateB.getTime();
      });
  };

  // Calculate booking duration in hours (for visual height)
  const getBookingDurationHours = (booking) => {
    if (!booking.start_datetime || !booking.end_datetime) return 1;
    const start = parseLocalDateTime(booking.start_datetime);
    const end = parseLocalDateTime(booking.end_datetime);
    if (!start || !end) return 1;
    const durationMs = end.getTime() - start.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);
    return Math.max(durationHours, 0.5); // Minimum 30 min visual height
  };

  // Calculate booking start offset in minutes (for precise positioning)
  const getBookingStartOffset = (booking) => {
    if (!booking.start_datetime) return 0;
    const start = parseLocalDateTime(booking.start_datetime);
    if (!start) return 0;
    return start.getMinutes();
  };

  // Calculate overlapping bookings layout (for side-by-side display)
  const calculateOverlappingLayout = (bookingsInSlot, allBookingsForDay) => {
    if (!bookingsInSlot.length) return [];
    
    // Find all bookings that overlap with bookings in this slot
    const getOverlappingBookings = (booking) => {
      const start = parseLocalDateTime(booking.start_datetime);
      const end = parseLocalDateTime(booking.end_datetime);
      if (!start || !end) return [];
      
      return allBookingsForDay.filter(b => {
        if (b.booking_id === booking.booking_id) return false;
        const bStart = parseLocalDateTime(b.start_datetime);
        const bEnd = parseLocalDateTime(b.end_datetime);
        if (!bStart || !bEnd) return false;
        // Check if they overlap
        return (bStart < end && bEnd > start);
      });
    };
    
    // Group overlapping bookings
    const groups = [];
    const processed = new Set();
    
    allBookingsForDay.forEach(booking => {
      if (processed.has(booking.booking_id)) return;
      
      const group = [booking];
      processed.add(booking.booking_id);
      
      const overlapping = getOverlappingBookings(booking);
      overlapping.forEach(b => {
        if (!processed.has(b.booking_id)) {
          group.push(b);
          processed.add(b.booking_id);
        }
      });
      
      if (group.length > 1) {
        groups.push(group);
      }
    });
    
    // Create layout map
    const layoutMap = new Map();
    
    groups.forEach(group => {
      const sortedGroup = group.sort((a, b) => {
        const aStart = parseLocalDateTime(a.start_datetime);
        const bStart = parseLocalDateTime(b.start_datetime);
        return (aStart?.getTime() || 0) - (bStart?.getTime() || 0);
      });
      
      sortedGroup.forEach((booking, index) => {
        layoutMap.set(booking.booking_id, {
          width: 100 / sortedGroup.length,
          left: (100 / sortedGroup.length) * index,
          total: sortedGroup.length
        });
      });
    });
    
    return layoutMap;
  };

  // Get Google events that START at a specific hour
  const getGoogleEventsStartingAtSlot = (day, hour) => {
    if (!showGoogleEvents || !googleEvents.length) return [];
    
    return googleEvents.filter(event => {
      if (!event.start_datetime) return false;
      const eventDate = parseLocalDateTime(event.start_datetime);
      if (!eventDate) return false;
      return isSameDay(eventDate, day) && eventDate.getHours() === hour;
    });
  };

  // Calculate Google event duration in hours
  const getGoogleEventDurationHours = (event) => {
    if (!event.start_datetime || !event.end_datetime) return 1;
    const start = parseLocalDateTime(event.start_datetime);
    const end = parseLocalDateTime(event.end_datetime);
    if (!start || !end) return 1;
    const durationMs = end.getTime() - start.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);
    return Math.max(durationHours, 0.5);
  };

  // Get Google event start offset in minutes
  const getGoogleEventStartOffset = (event) => {
    if (!event.start_datetime) return 0;
    const start = parseLocalDateTime(event.start_datetime);
    if (!start) return 0;
    return start.getMinutes();
  };

  const getStaffName = (booking) => {
    if (booking.staff_id) {
      const staffMember = staff.find(s => s.staff_id === booking.staff_id);
      if (staffMember) return staffMember.name;
    }
    const calendar = calendars.find(c => c.calendar_id === booking.calendar_id);
    if (calendar?.staff_id) {
      const staffMember = staff.find(s => s.staff_id === calendar.staff_id);
      return staffMember?.name || "Staff";
    }
    return null;
  };

  const getStaffImage = (booking) => {
    if (booking.staff_id) {
      const staffMember = staff.find(s => s.staff_id === booking.staff_id);
      if (staffMember?.image_url) return staffMember.image_url;
    }
    const calendar = calendars.find(c => c.calendar_id === booking.calendar_id);
    if (calendar?.staff_id) {
      const staffMember = staff.find(s => s.staff_id === calendar.staff_id);
      return staffMember?.image_url;
    }
    return null;
  };

  return (
    <>
    <Card className={`border-slate-100 ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto rounded-none' : ''}`}>
      <CardHeader className="pb-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Calendario Prenotazioni
            {isFullscreen && (
              <Button variant="ghost" size="icon" className="ml-2" onClick={toggleFullscreen}>
                <X className="h-5 w-5" />
              </Button>
            )}
          </CardTitle>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-slate-400" />
              <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: cat.color || '#6B7280' }}></div>
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Staff Filter */}
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-slate-400" />
              <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutto lo staff</SelectItem>
                  {staff.map((s) => (
                    <SelectItem key={s.staff_id} value={s.staff_id}>
                      <div className="flex items-center gap-2">
                        {s.image_url ? (
                          <img src={s.image_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                        ) : (
                          <div className="w-4 h-4 rounded-full bg-slate-200"></div>
                        )}
                        {s.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Google Calendar Toggle */}
            {googleCalendarConnected && (
              <div className="flex items-center gap-2">
                <Button
                  variant={showGoogleEvents ? "default" : "outline"}
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowGoogleEvents(!showGoogleEvents)}
                  title={showGoogleEvents ? "Nascondi eventi Google Calendar" : "Mostra eventi Google Calendar"}
                >
                  <Calendar className="h-4 w-4" />
                  <span className="hidden sm:inline">Google Cal</span>
                  {googleEvents.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5">{googleEvents.length}</Badge>
                  )}
                </Button>
              </div>
            )}

            {/* View Mode Toggle & Navigation */}
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                <Button 
                  variant={viewMode === "week" ? "default" : "ghost"} 
                  size="sm"
                  className="rounded-none"
                  onClick={() => setViewMode("week")}
                >
                  Settimana
                </Button>
                <Button 
                  variant={viewMode === "staff" ? "default" : "ghost"} 
                  size="sm"
                  className="rounded-none"
                  onClick={() => setViewMode("staff")}
                >
                  Per Staff
                </Button>
              </div>

              {/* Date Picker */}
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setDatePickerOpen(true)}>
                <CalendarIcon className="h-4 w-4" />
                {format(currentDate, "d MMM", { locale: it })}
              </Button>

              {/* Navigation */}
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={() => viewMode === "week" ? navigateWeek("prev") : navigateDay("prev")}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Oggi
                </Button>
                <Button variant="outline" size="icon" onClick={() => viewMode === "week" ? navigateWeek("next") : navigateDay("next")}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={toggleFullscreen} title={isFullscreen ? "Esci da schermo intero" : "Schermo intero"}>
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
                
                {/* Zoom Controls */}
                <div className="flex items-center border rounded-lg overflow-hidden ml-1" data-testid="zoom-controls">
                  <button
                    className="px-2 py-1.5 text-sm hover:bg-slate-100 transition-colors disabled:opacity-30"
                    onClick={() => {
                      const idx = ZOOM_LEVELS.indexOf(zoomLevel);
                      if (idx > 0) setZoomLevel(ZOOM_LEVELS[idx - 1]);
                    }}
                    disabled={zoomLevel === ZOOM_LEVELS[0]}
                    title="Riduci zoom"
                  >
                    <Minimize2 className="h-3.5 w-3.5" />
                  </button>
                  <span className="px-2 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 border-l border-r min-w-[70px] text-center" data-testid="zoom-label">
                    {ZOOM_LABELS[ZOOM_LEVELS.indexOf(zoomLevel)]}
                  </span>
                  <button
                    className="px-2 py-1.5 text-sm hover:bg-slate-100 transition-colors disabled:opacity-30"
                    onClick={() => {
                      const idx = ZOOM_LEVELS.indexOf(zoomLevel);
                      if (idx < ZOOM_LEVELS.length - 1) setZoomLevel(ZOOM_LEVELS[idx + 1]);
                    }}
                    disabled={zoomLevel === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
                    title="Aumenta zoom"
                  >
                    <Maximize2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-slate-500 mt-2">
          {viewMode === "week" 
            ? `${format(weekStart, "d MMMM", { locale: it })} - ${format(addDays(weekStart, 6), "d MMMM yyyy", { locale: it })}`
            : format(currentDate, "EEEE d MMMM yyyy", { locale: it })
          }
          <span className="ml-2 text-slate-400">({filteredBookings.length} prenotazioni)</span>
        </p>
      </CardHeader>
      
      <CardContent>
        {/* Category Legend */}
        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b">
            <span className="text-sm text-slate-500">Categorie:</span>
            {categories.map((cat) => {
              const count = filteredBookings.filter(b => getBookingCategory(b)?.category_id === cat.category_id).length;
              const catColor = cat.color || '#6B7280';
              return (
                <button
                  key={cat.category_id}
                  onClick={() => setSelectedCategoryId(selectedCategoryId === cat.category_id ? "all" : cat.category_id)}
                  style={{
                    backgroundColor: hexToRgba(catColor, selectedCategoryId === cat.category_id ? 0.25 : 0.15),
                    borderColor: selectedCategoryId === cat.category_id ? catColor : 'transparent',
                    color: catColor
                  }}
                  className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs transition-all font-medium ${
                    selectedCategoryId === cat.category_id ? 'border-2' : 'border-2 border-transparent hover:opacity-80'
                  }`}
                >
                  <span>{cat.name}</span>
                  <span className="bg-white/50 px-1.5 rounded-full">{count}</span>
                </button>
              );
            })}
            <button
              onClick={() => setSelectedCategoryId("all")}
              className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs transition-all ${
                selectedCategoryId === "all" 
                  ? "bg-slate-200 border-2 border-slate-400 text-slate-800" 
                  : "bg-slate-100 text-slate-600 hover:opacity-80 border-2 border-transparent"
              }`}
            >
              <span className="font-medium">Senza categoria</span>
              <span className="bg-white/50 px-1.5 rounded-full">
                {filteredBookings.filter(b => !getBookingCategory(b)).length}
              </span>
            </button>
          </div>
        )}

        {/* Week View */}
        {viewMode === "week" && (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <div className="min-w-[900px]">
            {/* Header - Days */}
            <div className="grid grid-cols-8 border-b">
              <div className="p-2 text-center text-sm font-medium text-slate-500">Ora</div>
              {weekDays.map((day) => {
                const dayBookings = getBookingsForDay(day);
                return (
                  <div
                    key={day.toISOString()}
                    className={`p-2 text-center border-l ${
                      isSameDay(day, new Date()) ? "bg-primary/5" : ""
                    }`}
                  >
                    <div className="text-xs text-slate-500 uppercase">
                      {format(day, "EEEE", { locale: it })}
                    </div>
                    <div className={`text-xl font-bold ${
                      isSameDay(day, new Date()) ? "text-primary" : "text-slate-900"
                    }`}>
                      {format(day, "d")}
                    </div>
                    {dayBookings.length > 0 && (
                      <div className="flex justify-center gap-0.5 mt-1">
                        {categories.slice(0, 5).map((cat) => {
                          const catCount = dayBookings.filter(b => getBookingCategory(b)?.category_id === cat.category_id).length;
                          if (catCount === 0) return null;
                          return (
                            <div key={cat.category_id} className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color || '#6B7280' }} title={`${cat.name}: ${catCount}`}></div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Time Grid */}
            <div className="relative">
              {hours.map((hour) => (
                <div key={hour} className="grid grid-cols-8 border-b relative" style={{ minHeight: `${pixelsPerHour}px`, height: `${pixelsPerHour}px` }}>
                  <div className="p-2 text-sm text-slate-500 text-right pr-3 border-r bg-slate-50/50">
                    {hour}:00
                    {pixelsPerHour >= 120 && (
                      <div className="text-[10px] text-slate-300 mt-auto" style={{ marginTop: `${pixelsPerHour / 2 - 20}px` }}>
                        {hour}:30
                      </div>
                    )}
                  </div>
                  {weekDays.map((day) => {
                    const slotBookings = getBookingsStartingAtSlot(day, hour);
                    const slotGoogleEvents = getGoogleEventsStartingAtSlot(day, hour);
                    const slotBlocks = getStaffBlocksStartingAtSlot(day, hour);
                    const dayBookings = getBookingsForDay(day);
                    const overlappingLayout = calculateOverlappingLayout(slotBookings, dayBookings);
                    return (
                      <div
                        key={`${day.toISOString()}-${hour}`}
                        className={`border-l relative group overflow-visible ${
                          isSameDay(day, new Date()) ? "bg-primary/5" : ""
                        } ${dragOverSlot?.day === day.toISOString() && dragOverSlot?.hour === hour ? "bg-blue-100" : ""}`}
                        style={{ minHeight: `${pixelsPerHour}px`, height: `${pixelsPerHour}px` }}
                        onDragOver={(e) => handleDragOver(e, day, hour)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, day, hour)}
                      >
                        {/* Half-hour indicator line */}
                        {pixelsPerHour >= 100 && (
                          <div className="absolute left-0 right-0 border-t border-dashed border-slate-200/70" style={{ top: `${pixelsPerHour / 2}px` }} />
                        )}
                        {/* Add booking button - always visible on hover */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openBookingDialog(day, hour);
                          }}
                          className="absolute top-1 right-1 z-20 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/20"
                          title="Aggiungi prenotazione"
                        >
                          <Plus className="h-4 w-4 text-primary" />
                        </button>
                        
                        {/* Bookings with dynamic height based on duration */}
                        {slotBookings.map((booking, idx) => {
                          const category = getBookingCategory(booking);
                          const staffColor = getBookingStaffColor(booking);
                          const isNewBooking = newBookingHighlight === booking.booking_id;
                          const customerIsNew = isNewCustomer(booking);
                          
                          // Calculate height based on duration
                          const durationHours = getBookingDurationHours(booking);
                          const heightPx = Math.max(durationHours * pixelsPerHour, Math.min(pixelsPerHour * 0.45, 45)); // proportional min height
                          const startOffset = getBookingStartOffset(booking);
                          const topOffsetPx = (startOffset / 60) * pixelsPerHour; // Offset based on minutes
                          
                          // Get layout for overlapping bookings
                          const layout = overlappingLayout.get(booking.booking_id);
                          const widthPercent = layout ? layout.width : 100;
                          const leftPercent = layout ? layout.left : 0;
                          
                          // Status-based text styling
                          const statusTextClass = booking.status === 'cancelled' ? 'text-red-600 line-through opacity-60' : '';
                          
                          return (
                            <div
                              key={booking.booking_id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, booking)}
                              onDragEnd={handleDragEnd}
                              onClick={() => openEditBooking(booking)}
                              className={`absolute text-xs p-1.5 rounded-lg border cursor-grab active:cursor-grabbing hover:shadow-lg hover:z-30 transition-all ${statusTextClass} ${isNewBooking ? 'ring-2 ring-green-500 ring-offset-1 animate-pulse' : ''}`}
                              style={{ 
                                top: `${topOffsetPx + 2}px`, 
                                height: `${heightPx - 4}px`,
                                left: `calc(${leftPercent}% + 2px)`,
                                width: `calc(${widthPercent}% - 4px)`,
                                zIndex: 10 + idx,
                                overflow: 'hidden',
                                backgroundColor: staffColor ? `${staffColor}18` : (booking.status === 'cancelled' ? '#fef2f2' : '#f0f9ff'),
                                borderColor: staffColor ? `${staffColor}50` : (booking.status === 'cancelled' ? '#fca5a5' : '#bae6fd'),
                                borderLeftWidth: '4px',
                                borderLeftColor: staffColor || (booking.status === 'cancelled' ? '#ef4444' : '#3b82f6'),
                                color: booking.status === 'cancelled' ? '#991b1b' : '#1e293b'
                              }}
                              title={`${booking.customer_name} - ${booking.status === 'confirmed' ? 'Confermato' : booking.status === 'pending' ? 'In attesa' : booking.status === 'cancelled' ? 'Annullato' : booking.status} - Trascina per spostare`}
                            >
                              {/* Cliente con badge nuovo e staff avatar inline */}
                              <div className="font-semibold truncate flex items-center gap-1">
                                {(() => {
                                  const assignments = getBookingStaffAssignments(booking);
                                  const primaryStaff = assignments[0];
                                  return primaryStaff ? (
                                    primaryStaff.staff_image ? (
                                      <img src={primaryStaff.staff_image} alt={primaryStaff.staff_name} className="w-5 h-5 rounded-full object-cover border border-white shadow-sm shrink-0 inline-block" />
                                    ) : (
                                      <div className="w-5 h-5 rounded-full flex items-center justify-center border border-white shadow-sm shrink-0 inline-flex text-white"
                                        style={{ backgroundColor: primaryStaff.staff_color || '#6366f1', fontSize: '9px', fontWeight: 700 }}
                                      >
                                        {primaryStaff.staff_name?.charAt(0)}
                                      </div>
                                    )
                                  ) : null;
                                })()}
                                <span className="truncate">{booking.customer_name}</span>
                                {customerIsNew && (
                                  <span className="bg-blue-500 text-white text-[7px] px-1 py-0.5 rounded font-bold shrink-0">N</span>
                                )}
                              </div>
                              
                              {/* Orario - sempre visibile */}
                              <div className="flex items-center gap-1 text-[10px] opacity-80 mt-0.5">
                                <Clock className="h-2.5 w-2.5 shrink-0" />
                                <span>
                                  {booking.start_datetime && parseLocalDateTime(booking.start_datetime) && format(parseLocalDateTime(booking.start_datetime), "HH:mm")}
                                  {booking.end_datetime && parseLocalDateTime(booking.end_datetime) && `-${format(parseLocalDateTime(booking.end_datetime), "HH:mm")}`}
                                </span>
                              </div>
                              
                              {/* Categoria e Staff - solo se c'è spazio */}
                              {heightPx > pixelsPerHour && (
                                <div className="flex items-center justify-between mt-1">
                                  {category && (
                                    <span className="text-[9px] uppercase tracking-wide opacity-60 truncate">
                                      {category.name}
                                    </span>
                                  )}
                                  <div className="flex items-center -space-x-1">
                                    {(() => {
                                      const assignments = getBookingStaffAssignments(booking);
                                      if (assignments.length > 0) {
                                        return assignments.map((a, i) => (
                                          a.staff_image ? (
                                            <img key={i} src={a.staff_image} alt={a.staff_name} title={a.staff_name} className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm shrink-0" style={{ borderColor: a.staff_color || '#fff' }} />
                                          ) : (
                                            <div key={i} title={a.staff_name} className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-white shadow-sm shrink-0 text-white"
                                              style={{ backgroundColor: a.staff_color || '#94a3b8' }}
                                            >
                                              <span className="text-[10px] font-bold">{a.staff_name?.charAt(0)}</span>
                                            </div>
                                          )
                                        ));
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </div>
                              )}
                              
                              {/* Servizi - solo se altezza > 90 */}
                              {heightPx > (pixelsPerHour * 1.2) && (
                                <div className="truncate opacity-70 text-[9px] mt-0.5">
                                  {booking.services?.map(s => s.name).join(", ") || booking.services_details?.map(s => s.name).join(", ")}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Google Calendar Events with dynamic height */}
                        {showGoogleEvents && slotGoogleEvents.map((event, idx) => {
                          const durationHours = getGoogleEventDurationHours(event);
                          const heightPx = Math.max(durationHours * pixelsPerHour, Math.min(pixelsPerHour * 0.45, 45));
                          const startOffset = getGoogleEventStartOffset(event);
                          const topOffsetPx = (startOffset / 60) * pixelsPerHour;
                          
                          return (
                            <div
                              key={event.event_id}
                              className="absolute left-1 right-1 text-xs p-2 rounded-lg border bg-purple-100 border-purple-300 text-purple-800"
                              style={{ 
                                top: `${topOffsetPx + 2}px`, 
                                height: `${heightPx - 4}px`,
                                zIndex: 5 + idx,
                                overflow: 'hidden'
                              }}
                              title={`Google Calendar: ${event.summary}`}
                            >
                              <div className="flex items-center gap-1 mb-1">
                                <Calendar className="h-3 w-3" />
                                <span className="font-semibold text-[10px] uppercase tracking-wide">Google Cal</span>
                              </div>
                              <div className="font-semibold truncate">{event.summary}</div>
                              <div className="flex items-center gap-1 mt-1 text-[10px] opacity-70">
                                <Clock className="h-3 w-3" />
                                {event.start_datetime && parseLocalDateTime(event.start_datetime) && format(parseLocalDateTime(event.start_datetime), "HH:mm")}
                                {event.end_datetime && parseLocalDateTime(event.end_datetime) && `-${format(parseLocalDateTime(event.end_datetime), "HH:mm")}`}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Staff Blocks (Pause/Riposi) */}
                        {slotBlocks.map((block, idx) => {
                          const durationHours = getBlockDurationHours(block);
                          const heightPx = Math.max(durationHours * pixelsPerHour, Math.min(pixelsPerHour * 0.4, 40));
                          const startOffset = block.start_datetime ? parseLocalDateTime(block.start_datetime)?.getMinutes() || 0 : 0;
                          const topOffsetPx = (startOffset / 60) * pixelsPerHour;
                          const isBreak = block.block_type === "break";
                          const isDayOff = block.block_type === "day_off";
                          
                          // Find staff name
                          const blockStaff = staff.find(s => s.staff_id === block.staff_id);
                          
                          return (
                            <div
                              key={block.block_id}
                              draggable={!block.is_absence}
                              onDragStart={(e) => {
                                setDraggedBlock(block);
                                e.dataTransfer.setData("blockId", block.block_id);
                                e.dataTransfer.setData("isBlock", "true");
                                e.dataTransfer.effectAllowed = 'move';
                                e.currentTarget.style.opacity = '0.5';
                              }}
                              onDragEnd={(e) => {
                                e.currentTarget.style.opacity = '1';
                                setDraggedBlock(null);
                              }}
                              onClick={() => openBlockDialog(block)}
                              className={`absolute left-1 right-1 text-xs p-2 rounded-lg border cursor-grab active:cursor-grabbing hover:shadow-lg transition-all ${
                                isDayOff 
                                  ? "bg-red-100 border-red-300 text-red-800 hover:bg-red-200" 
                                  : "bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200"
                              }`}
                              style={{ 
                                top: `${topOffsetPx + 2}px`, 
                                height: `${heightPx - 4}px`,
                                zIndex: 5 + idx,
                                overflow: 'hidden'
                              }}
                              title={`${isDayOff ? "Riposo" : "Pausa"}: ${block.notes || ""} - Trascina per spostare, clicca per eliminare`}
                            >
                              <div className="flex items-center gap-1 mb-1">
                                {isDayOff ? <CalendarX className="h-3 w-3" /> : <Coffee className="h-3 w-3" />}
                                <span className="font-semibold text-[10px] uppercase tracking-wide">
                                  {isDayOff ? "Riposo" : "Pausa"}
                                </span>
                              </div>
                              {blockStaff && <div className="font-semibold truncate text-[10px]">{blockStaff.name}</div>}
                              <div className="flex items-center gap-1 mt-0.5 text-[10px] opacity-70">
                                <Clock className="h-2.5 w-2.5" />
                                {block.start_datetime && parseLocalDateTime(block.start_datetime) && format(parseLocalDateTime(block.start_datetime), "HH:mm")}
                                {block.end_datetime && parseLocalDateTime(block.end_datetime) && `-${format(parseLocalDateTime(block.end_datetime), "HH:mm")}`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Staff View - Daily by Staff */}
        {viewMode === "staff" && sortedStaff.length > 0 && (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <div style={{ minWidth: `${Math.max(600, sortedStaff.length * 150 + 80)}px` }}>
            {/* Header - Staff Names */}
            <div className="grid border-b" style={{ gridTemplateColumns: `80px repeat(${sortedStaff.length}, 1fr)` }}>
              <div className="p-2 text-center text-sm font-medium text-slate-500">Ora</div>
              {sortedStaff.map((s) => (
                <div key={s.staff_id} className="p-3 text-center border-l" style={{ borderTopWidth: '3px', borderTopColor: s.color || '#e2e8f0' }}>
                  <div className="flex flex-col items-center">
                    {s.image_url ? (
                      <img src={s.image_url} alt="" className="w-14 h-14 rounded-full object-cover mb-1 border-2" style={{ borderColor: s.color || '#e2e8f0' }} />
                    ) : (
                      <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold mb-1"
                        style={{ backgroundColor: s.color || '#6366f1' }}
                      >
                        {s.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-medium truncate max-w-full">{s.name}</span>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Time Grid for Staff View */}
            <div className="relative">
              {hours.map((hour) => {
                // Calculate max bookings in any staff slot for this hour to determine row height
                const maxBookingsInHour = Math.max(1, ...sortedStaff.map(s => {
                  const bookings = getBookingsStartingAtStaffSlot(s, hour);
                  return bookings.length;
                }));
                
                // Dynamic row height based on zoom level and stacked bookings
                const baseHeight = Math.max(pixelsPerHour, 70);
                const rowHeight = baseHeight + (maxBookingsInHour > 1 ? (maxBookingsInHour - 1) * 52 : 0);
                
                return (
                <div key={hour} className="grid border-b relative" style={{ gridTemplateColumns: `80px repeat(${sortedStaff.length}, 1fr)`, minHeight: `${rowHeight}px` }}>
                  <div className="p-2 text-sm text-slate-500 text-right pr-3 border-r bg-slate-50/50">
                    {hour}:00
                    {pixelsPerHour >= 120 && (
                      <div className="text-[10px] text-slate-300" style={{ marginTop: `${Math.max(pixelsPerHour / 2 - 20, 10)}px` }}>
                        {hour}:30
                      </div>
                    )}
                  </div>
                  {sortedStaff.map((staffMember) => {
                    const slotBookings = getBookingsStartingAtStaffSlot(staffMember, hour);
                    const slotBlocks = getStaffBlocksStartingAtSlot(currentDate, hour, staffMember.staff_id);
                    return (
                      <div
                        key={`${staffMember.staff_id}-${hour}`}
                        className={`border-l relative group ${dragOverSlot?.day === currentDate.toISOString() && dragOverSlot?.hour === hour && dragOverSlot?.staffId === staffMember.staff_id ? "bg-blue-100" : ""}`}
                        style={{ minHeight: `${rowHeight}px` }}
                        onDragOver={(e) => handleDragOver(e, currentDate, hour, staffMember.staff_id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, currentDate, hour, staffMember.staff_id)}
                      >
                        {/* Half-hour indicator line */}
                        {pixelsPerHour >= 100 && (
                          <div className="absolute left-0 right-0 border-t border-dashed border-slate-200/70" style={{ top: `${pixelsPerHour / 2}px` }} />
                        )}
                        {/* Add booking button - always visible on hover */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setBookingForm(prev => ({...prev, staff_id: staffMember.staff_id}));
                            openBookingDialog(currentDate, hour, staffMember.staff_id);
                          }}
                          className="absolute top-1 right-1 z-20 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/20"
                          title="Aggiungi prenotazione"
                        >
                          <Plus className="h-4 w-4 text-primary" />
                        </button>
                        
                        {/* Bookings stacked vertically using flexbox */}
                        <div className="flex flex-col gap-1 p-1">
                          {slotBookings.map((booking, idx) => {
                            const category = getBookingCategory(booking);
                            const staffColor = staffMember.color;
                            const isNewBooking = newBookingHighlight === booking.booking_id;
                            const customerIsNew = isNewCustomer(booking);
                            const statusTextClass = booking.status === 'cancelled' ? 'line-through opacity-60' : '';
                            
                            return (
                              <div
                                key={booking.booking_id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, booking)}
                                onDragEnd={handleDragEnd}
                                onClick={() => openEditBooking(booking)}
                                className={`text-xs p-1.5 rounded-lg border cursor-grab active:cursor-grabbing hover:shadow-lg transition-all ${statusTextClass} ${isNewBooking ? 'ring-2 ring-green-500 ring-offset-1 animate-pulse' : ''}`}
                                style={{ 
                                  minHeight: '48px',
                                  backgroundColor: staffColor ? `${staffColor}18` : (booking.status === 'cancelled' ? '#fef2f2' : '#f0f9ff'),
                                  borderColor: staffColor ? `${staffColor}50` : (booking.status === 'cancelled' ? '#fca5a5' : '#bae6fd'),
                                  borderLeftWidth: '4px',
                                  borderLeftColor: staffColor || (booking.status === 'cancelled' ? '#ef4444' : '#3b82f6'),
                                  color: booking.status === 'cancelled' ? '#991b1b' : '#1e293b'
                                }}
                                title={`${booking.customer_name} - ${booking.start_datetime && parseLocalDateTime(booking.start_datetime) ? format(parseLocalDateTime(booking.start_datetime), "HH:mm") : ''}-${booking.end_datetime && parseLocalDateTime(booking.end_datetime) ? format(parseLocalDateTime(booking.end_datetime), "HH:mm") : ''} - Trascina per spostare`}
                              >
                                {/* Cliente con badge nuovo inline */}
                                <div className="font-semibold truncate flex items-center gap-1">
                                  {booking.customer_name}
                                  {customerIsNew && (
                                    <span className="bg-blue-500 text-white text-[7px] px-1 py-0.5 rounded font-bold shrink-0">N</span>
                                  )}
                                </div>
                                
                                {/* Orario - sempre visibile */}
                                <div className="flex items-center gap-1 text-[10px] opacity-80 mt-0.5">
                                  <Clock className="h-2.5 w-2.5 shrink-0" />
                                  <span>
                                    {booking.start_datetime && parseLocalDateTime(booking.start_datetime) && format(parseLocalDateTime(booking.start_datetime), "HH:mm")}
                                    {booking.end_datetime && parseLocalDateTime(booking.end_datetime) && `-${format(parseLocalDateTime(booking.end_datetime), "HH:mm")}`}
                                  </span>
                                </div>
                                
                                {/* Servizi */}
                                <div className="truncate opacity-70 text-[9px] mt-0.5">
                                  {booking.services?.map(s => s.name).join(", ") || booking.services_details?.map(s => s.name).join(", ")}
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Staff Blocks (Pause/Riposi) for this staff member */}
                          {slotBlocks.map((block, idx) => {
                            const isDayOff = block.block_type === "day_off";
                            
                            return (
                              <div
                                key={block.block_id}
                                draggable={!block.is_absence}
                                onDragStart={(e) => {
                                  setDraggedBlock(block);
                                  e.dataTransfer.setData("blockId", block.block_id);
                                  e.dataTransfer.setData("isBlock", "true");
                                  e.dataTransfer.effectAllowed = 'move';
                                  e.currentTarget.style.opacity = '0.5';
                                }}
                                onDragEnd={(e) => {
                                  e.currentTarget.style.opacity = '1';
                                  setDraggedBlock(null);
                                }}
                                onClick={() => openBlockDialog(block)}
                                className={`text-xs p-1.5 rounded-lg border cursor-grab active:cursor-grabbing hover:shadow-lg transition-all ${
                                  isDayOff 
                                    ? "bg-red-100 border-red-300 text-red-800 hover:bg-red-200" 
                                    : "bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200"
                                }`}
                                style={{ minHeight: '48px' }}
                                title={`${isDayOff ? "Riposo" : "Pausa"}: ${block.notes || ""} - Trascina per spostare, clicca per eliminare`}
                              >
                                <div className="flex items-center gap-1">
                                  {isDayOff ? <CalendarX className="h-3 w-3" /> : <Coffee className="h-3 w-3" />}
                                  <span className="font-semibold text-[10px] uppercase">
                                    {isDayOff ? "Riposo" : "Pausa"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 mt-0.5 text-[10px] opacity-70">
                                  <Clock className="h-2.5 w-2.5" />
                                  {block.start_datetime && parseLocalDateTime(block.start_datetime) && format(parseLocalDateTime(block.start_datetime), "HH:mm")}
                                  {block.end_datetime && parseLocalDateTime(block.end_datetime) && `-${format(parseLocalDateTime(block.end_datetime), "HH:mm")}`}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                );
              })}
            </div>
          </div>
        </div>
        )}

        {/* No Staff Message for Staff View */}
        {viewMode === "staff" && staff.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nessuno staff configurato.</p>
            <p className="text-sm">Aggiungi membri dello staff per utilizzare questa vista.</p>
          </div>
        )}

        {/* Staff Legend */}
        {viewMode === "week" && staff.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t">
            <span className="text-sm text-slate-500">Staff:</span>
            {staff.map((s) => (
              <button
                key={s.staff_id}
                onClick={() => setSelectedStaffId(selectedStaffId === s.staff_id ? "all" : s.staff_id)}
                className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs transition-all ${
                  selectedStaffId === s.staff_id 
                    ? "bg-primary text-white" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {s.image_url ? (
                  <img src={s.image_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-slate-300 flex items-center justify-center">
                    <User className="h-4 w-4 text-slate-500" />
                  </div>
                )}
                <span className="font-medium">{s.name}</span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Manual Booking Dialog */}
    <Dialog open={bookingDialogOpen} onOpenChange={(open) => {
      setBookingDialogOpen(open);
      if (!open) {
        setShowSuggestions(false);
        setCustomerSuggestions([]);
      }
    }}>
      <DialogContent className="max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>Nuova Prenotazione Manuale</DialogTitle>
        </DialogHeader>
        {selectedSlot && (
          <div className="flex items-center gap-2 text-sm text-slate-500 px-6 pb-2 shrink-0">
            <Calendar className="h-4 w-4" />
            {format(selectedSlot.day, "EEEE d MMMM yyyy", { locale: it })}
          </div>
        )}
        <form onSubmit={handleCreateBooking} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 space-y-4 pb-4">
          {/* Time Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Orario *
            </Label>
            <Input 
              type="time"
              value={bookingForm.booking_time}
              onChange={(e) => setBookingForm({...bookingForm, booking_time: e.target.value})}
              required
              data-testid="booking-time-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 relative">
              <Label>Nome Cliente *</Label>
              <Input 
                value={bookingForm.customer_name} 
                onChange={(e) => setBookingForm({...bookingForm, customer_name: e.target.value})} 
                onFocus={() => bookingForm.customer_name.length >= 2 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Inizia a digitare..."
                autoComplete="off"
                required 
                data-testid="booking-customer-name"
              />
              {/* Customer suggestions dropdown */}
              {showSuggestions && customerSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {customerSuggestions.map((customer, idx) => (
                    <div
                      key={customer.customer_id || idx}
                      className="px-3 py-2 hover:bg-slate-100 cursor-pointer border-b last:border-b-0"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectCustomer(customer);
                      }}
                      data-testid={`customer-suggestion-${idx}`}
                    >
                      <div className="font-medium text-sm">{customer.name}</div>
                      <div className="text-xs text-slate-500">
                        {customer.email && <span>{customer.email}</span>}
                        {customer.email && customer.phone && <span> • </span>}
                        {customer.phone && <span>{customer.phone}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {searchingCustomers && (
                <div className="absolute right-3 top-9 text-slate-400">
                  <Clock className="h-4 w-4 animate-spin" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                type="email"
                value={bookingForm.customer_email} 
                onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})} 
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Telefono</Label>
            <Input 
              value={bookingForm.customer_phone} 
              onChange={(e) => setBookingForm({...bookingForm, customer_phone: e.target.value})} 
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            {/* STAFF FIRST - then services filtered by staff */}
            <div className="space-y-2">
              <Label>Staff *</Label>
              <Select value={bookingForm.staff_id} onValueChange={(v) => setBookingForm({...bookingForm, staff_id: v, service_ids: []})}>
                <SelectTrigger data-testid="booking-staff-select">
                  <SelectValue placeholder="Seleziona staff" />
                </SelectTrigger>
                <SelectContent>
                  {staff.filter(s => s.is_active !== false).map((s) => (
                    <SelectItem key={s.staff_id} value={s.staff_id}>
                      <div className="flex items-center gap-2">
                        {s.image_url ? (
                          <img src={s.image_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                        ) : (
                          <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
                            style={{ backgroundColor: s.color || '#6366f1' }}>
                            {s.name?.charAt(0)}
                          </div>
                        )}
                        <span>{s.name}</span>
                        <span className="text-xs text-slate-400 ml-auto">
                          {getStaffServiceIds(s).length} servizi
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Servizi * <Badge variant="secondary" className="ml-1 text-xs">{bookingForm.service_ids.length}</Badge></Label>
              <div className="relative">
                {!bookingForm.staff_id ? (
                  <div className="flex items-center h-10 px-3 border rounded-md bg-slate-50 text-sm text-slate-400">
                    Seleziona prima lo staff
                  </div>
                ) : (
                  <>
                    <Input
                      placeholder="Cerca servizio..."
                      value={bookingForm._serviceSearch || ""}
                      onChange={(e) => setBookingForm({...bookingForm, _serviceSearch: e.target.value})}
                      onClick={() => setBookingForm({...bookingForm, _serviceDropdownOpen: true})}
                      data-testid="service-search-input"
                    />
                    {bookingForm.service_ids.length > 0 && !bookingForm._serviceDropdownOpen && (
                      <div className="absolute inset-0 bg-white flex items-center px-3 rounded-md border cursor-pointer gap-1 overflow-hidden"
                        onClick={() => setBookingForm({...bookingForm, _serviceDropdownOpen: true, _serviceSearch: ""})}>
                        {bookingForm.service_ids.map(sid => {
                          const svc = services.find(s => s.service_id === sid);
                          return svc ? <Badge key={sid} variant="secondary" className="text-xs truncate max-w-[120px]">{svc.name}</Badge> : null;
                        })}
                      </div>
                    )}
                    {bookingForm._serviceDropdownOpen && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {(() => {
                          const search = (bookingForm._serviceSearch || "").toLowerCase();
                          const selectedStaffMember = staff.find(s => s.staff_id === bookingForm.staff_id);
                          const staffServiceIds = getStaffServiceIds(selectedStaffMember);
                          // Filter: only services assigned to the selected staff
                          const filteredServices = services.filter(s => 
                            s.is_active !== false && 
                            staffServiceIds.includes(s.service_id) &&
                            s.name.toLowerCase().includes(search)
                          );
                          const grouped = {};
                          filteredServices.forEach(s => {
                            const cat = categories.find(c => c.category_id === s.category_id);
                            const catName = cat?.name || "Senza categoria";
                            if (!grouped[catName]) grouped[catName] = [];
                            grouped[catName].push(s);
                          });
                          if (Object.keys(grouped).length === 0) {
                            return <div className="p-3 text-sm text-slate-400">
                              {staffServiceIds.length === 0 
                                ? "Nessun servizio assegnato a questo operatore" 
                                : "Nessun servizio trovato"}
                            </div>;
                          }
                          return Object.entries(grouped).map(([catName, catServices]) => (
                            <div key={catName}>
                              <div className="px-3 py-1.5 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide sticky top-0">
                                {catName}
                              </div>
                              {catServices.map(s => {
                                const isSelected = bookingForm.service_ids.includes(s.service_id);
                                return (
                                  <div
                                    key={s.service_id}
                                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-primary/5 flex justify-between items-center ${isSelected ? 'bg-primary/10 font-medium' : ''}`}
                                    onClick={() => {
                                      const newIds = isSelected
                                        ? bookingForm.service_ids.filter(id => id !== s.service_id)
                                        : [...bookingForm.service_ids, s.service_id];
                                      setBookingForm({...bookingForm, service_ids: newIds});
                                    }}
                                    data-testid={`service-option-${s.service_id}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <input type="checkbox" checked={isSelected} readOnly className="h-3.5 w-3.5 rounded border-slate-300" />
                                      <span>{s.name}</span>
                                    </div>
                                    <span className="text-xs text-slate-400">{s.duration_minutes} min - €{s.price}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ));
                        })()}
                        {bookingForm.service_ids.length > 0 && (
                          <div className="sticky bottom-0 bg-white border-t p-2">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-slate-500">
                                {bookingForm.service_ids.length} servizi — Durata: {services.filter(s => bookingForm.service_ids.includes(s.service_id)).reduce((sum, s) => sum + (s.duration_minutes || 0), 0)} min — €{services.filter(s => bookingForm.service_ids.includes(s.service_id)).reduce((sum, s) => sum + (s.price || 0), 0).toFixed(2)}
                              </span>
                              <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setBookingForm({...bookingForm, _serviceDropdownOpen: false})}>Chiudi</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Overlap detection warning */}
          {(() => {
            if (!bookingForm.staff_id || bookingForm.service_ids.length === 0 || !selectedSlot) return null;
            const totalDuration = services.filter(s => bookingForm.service_ids.includes(s.service_id)).reduce((sum, s) => sum + (s.duration_minutes || 30), 0);
            const bookingStart = new Date(selectedSlot.day);
            if (bookingForm.booking_time) {
              const [h, m] = bookingForm.booking_time.split(':').map(Number);
              bookingStart.setHours(h, m, 0, 0);
            } else {
              bookingStart.setHours(selectedSlot.hour, 0, 0, 0);
            }
            const bookingEnd = new Date(bookingStart);
            bookingEnd.setMinutes(bookingEnd.getMinutes() + totalDuration);
            
            // Find overlapping bookings for the selected staff
            const staffBookings = filteredBookings.filter(b => {
              if (b.status === 'cancelled') return false;
              if (!b.start_datetime || !b.end_datetime) return false;
              const bStart = parseLocalDateTime(b.start_datetime);
              const bEnd = parseLocalDateTime(b.end_datetime);
              if (!bStart || !bEnd) return false;
              if (!isSameDay(bStart, selectedSlot.day)) return false;
              // Check if involves selected staff
              const isForStaff = b.staff_id === bookingForm.staff_id || 
                bookingInvolvesStaff(b, { staff_id: bookingForm.staff_id });
              return isForStaff;
            });
            
            const overlaps = staffBookings.filter(b => {
              const bStart = parseLocalDateTime(b.start_datetime);
              const bEnd = parseLocalDateTime(b.end_datetime);
              // Overlap: bStart < bookingEnd AND bEnd > bookingStart
              return bStart < bookingEnd && bEnd > bookingStart;
            });
            
            if (overlaps.length === 0) return null;
            
            // Find suggested alternative slots
            const suggestions = [];
            const dayBookings = staffBookings
              .map(b => ({
                start: parseLocalDateTime(b.start_datetime),
                end: parseLocalDateTime(b.end_datetime),
                name: b.customer_name
              }))
              .filter(b => b.start && b.end)
              .sort((a, b) => a.start.getTime() - b.start.getTime());
            
            // Try slots: before first booking, between bookings, after last booking
            const dayStart = new Date(selectedSlot.day);
            dayStart.setHours(8, 0, 0, 0);
            const dayEnd = new Date(selectedSlot.day);
            dayEnd.setHours(20, 0, 0, 0);
            
            // Check gaps between bookings
            let checkFrom = dayStart;
            for (const b of dayBookings) {
              const gapMinutes = (b.start.getTime() - checkFrom.getTime()) / 60000;
              if (gapMinutes >= totalDuration) {
                const slotStart = new Date(checkFrom);
                suggestions.push(format(slotStart, "HH:mm"));
                if (suggestions.length >= 3) break;
              }
              if (b.end > checkFrom) checkFrom = new Date(b.end);
            }
            // After last booking
            if (suggestions.length < 3 && checkFrom < dayEnd) {
              const remaining = (dayEnd.getTime() - checkFrom.getTime()) / 60000;
              if (remaining >= totalDuration) {
                suggestions.push(format(checkFrom, "HH:mm"));
              }
            }
            
            return (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2" data-testid="overlap-warning">
                <div className="flex items-center gap-2 text-red-700 font-medium text-sm">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Sovrapposizione orario!
                </div>
                <div className="text-xs text-red-600 space-y-1">
                  {overlaps.map((b, i) => {
                    const bStart = parseLocalDateTime(b.start_datetime);
                    const bEnd = parseLocalDateTime(b.end_datetime);
                    return (
                      <div key={i} className="flex items-center gap-1">
                        <Clock className="h-3 w-3 shrink-0" />
                        <span>{b.customer_name}: {bStart ? format(bStart, "HH:mm") : "?"} - {bEnd ? format(bEnd, "HH:mm") : "?"}</span>
                      </div>
                    );
                  })}
                </div>
                {suggestions.length > 0 && (
                  <div className="pt-1 border-t border-red-200">
                    <div className="text-xs font-medium text-slate-700 mb-1.5">Orari disponibili:</div>
                    <div className="flex gap-2 flex-wrap">
                      {suggestions.map((time) => (
                        <button
                          key={time}
                          type="button"
                          className="px-3 py-1.5 bg-white border border-green-300 text-green-700 rounded-full text-xs font-medium hover:bg-green-50 transition-colors"
                          onClick={() => setBookingForm({...bookingForm, booking_time: time})}
                          data-testid={`suggestion-${time}`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="space-y-2">
            <Label>Note</Label>
            <Input 
              value={bookingForm.notes} 
              onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})} 
              placeholder="Note opzionali..."
            />
          </div>
          
          {/* Multi-session dates section */}
          {(() => {
            const selectedSvcs = services.filter(s => bookingForm.service_ids.includes(s.service_id));
            const multiSvc = selectedSvcs.find(s => s.session_type === 'multi' && (s.num_sessions || 1) > 1);
            if (!multiSvc) return null;
            
            const numSessions = multiSvc.num_sessions || 2;
            const intervalDays = multiSvc.session_interval_days || 7;
            
            // Auto-generate dates if not yet set
            if (!bookingForm.sessionDates || bookingForm.sessionDates.length !== numSessions) {
              const baseDate = selectedSlot?.day || new Date();
              const time = bookingForm.booking_time || format(new Date(), "HH:mm");
              const dates = Array.from({ length: numSessions }, (_, i) => ({
                date: format(addDays(new Date(baseDate), i * intervalDays), "yyyy-MM-dd"),
                time
              }));
              // Use setTimeout to avoid setting state during render
              setTimeout(() => setBookingForm(prev => ({...prev, sessionDates: dates})), 0);
              return null;
            }
            
            return (
              <div className="border-t pt-4 mt-2" data-testid="multi-session-dates">
                <Label className="text-sm font-medium text-blue-700 mb-3 flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Sedute programmate ({numSessions} sedute — ogni {intervalDays} giorni)
                </Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {bookingForm.sessionDates.map((sd, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-blue-50/50 rounded-lg border border-blue-100">
                      <Badge variant="outline" className="text-xs shrink-0 border-blue-300 text-blue-600">#{idx + 1}</Badge>
                      <Input 
                        type="date" 
                        value={sd.date} 
                        onChange={(e) => {
                          const newDates = [...bookingForm.sessionDates];
                          newDates[idx] = {...newDates[idx], date: e.target.value};
                          setBookingForm({...bookingForm, sessionDates: newDates});
                        }} 
                        className="h-8 text-sm flex-1"
                        data-testid={`session-date-${idx}`}
                      />
                      <Input 
                        type="time" 
                        value={sd.time} 
                        onChange={(e) => {
                          const newDates = [...bookingForm.sessionDates];
                          newDates[idx] = {...newDates[idx], time: e.target.value};
                          setBookingForm({...bookingForm, sessionDates: newDates});
                        }} 
                        className="h-8 text-sm w-24"
                        data-testid={`session-time-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          
          {/* Staff Availability Block Options */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm font-medium text-slate-700 mb-3 block">Blocca disponibilità staff</Label>
            <div className="space-y-3">
              {/* Pausa Option */}
              <div className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${bookingForm.is_break ? 'bg-amber-50 border-amber-200' : 'bg-slate-50'}`}>
                <input
                  type="checkbox"
                  id="is_break"
                  checked={bookingForm.is_break}
                  onChange={(e) => setBookingForm({
                    ...bookingForm, 
                    is_break: e.target.checked, 
                    is_day_off: false,
                    customer_name: e.target.checked ? "Pausa" : "",
                    service_ids: e.target.checked ? [] : bookingForm.service_ids
                  })}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                />
                <div className="flex-1">
                  <label htmlFor="is_break" className="font-medium text-sm cursor-pointer flex items-center gap-2">
                    <Coffee className="h-4 w-4 text-amber-600" />
                    Pausa
                  </label>
                  <p className="text-xs text-slate-500">Blocca lo staff per una pausa (es. pranzo)</p>
                  
                  {bookingForm.is_break && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Label className="text-xs">Da</Label>
                        <Input
                          type="time"
                          value={bookingForm.booking_time}
                          onChange={(e) => setBookingForm({...bookingForm, booking_time: e.target.value})}
                          className="h-8 w-24 text-xs"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <Label className="text-xs">A</Label>
                        <Input
                          type="time"
                          value={bookingForm.break_end_time}
                          onChange={(e) => setBookingForm({...bookingForm, break_end_time: e.target.value})}
                          className="h-8 w-24 text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Riposo Option */}
              <div className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${bookingForm.is_day_off ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}>
                <input
                  type="checkbox"
                  id="is_day_off"
                  checked={bookingForm.is_day_off}
                  onChange={(e) => setBookingForm({
                    ...bookingForm, 
                    is_day_off: e.target.checked, 
                    is_break: false,
                    customer_name: e.target.checked ? "Riposo" : "",
                    service_ids: e.target.checked ? [] : bookingForm.service_ids
                  })}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                />
                <div>
                  <label htmlFor="is_day_off" className="font-medium text-sm cursor-pointer flex items-center gap-2">
                    <CalendarX className="h-4 w-4 text-red-600" />
                    Riposo giornaliero
                  </label>
                  <p className="text-xs text-slate-500">Lo staff non è disponibile per tutto il giorno</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Send confirmation email checkbox */}
          {!bookingForm.is_break && !bookingForm.is_day_off && (
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200">
              <input
                type="checkbox"
                id="send_email"
                checked={bookingForm.send_email}
                onChange={(e) => setBookingForm({...bookingForm, send_email: e.target.checked})}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                data-testid="send-email-checkbox"
              />
              <label htmlFor="send_email" className="font-medium text-sm cursor-pointer flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" />
                Invia email di conferma al cliente
              </label>
            </div>
          )}
          </div>
          
          <div className="shrink-0 border-t px-6 py-3 bg-white">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBookingDialogOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="confirm-booking-btn">
                {bookingForm.is_break ? "Imposta Pausa" : bookingForm.is_day_off ? "Imposta Riposo" : "Crea Prenotazione"}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    {/* Edit Booking Dialog */}
    <Dialog open={editBookingDialogOpen} onOpenChange={setEditBookingDialogOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Modifica Prenotazione
            {customerHistory && !customerHistory.is_new_customer && (
              <Badge variant="secondary" className="ml-2">Cliente abituale</Badge>
            )}
            {customerHistory?.is_new_customer && (
              <Badge className="ml-2 bg-blue-500">Nuovo cliente</Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Edit Form */}
          <form onSubmit={handleUpdateBooking} className="space-y-4">
            {/* Booking Summary - All Services & Staff */}
            {selectedBooking && (selectedBooking.services_details?.length > 0 || selectedBooking.services?.length > 0) && (
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <Label className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Servizi prenotati</Label>
                <div className="mt-2 space-y-1.5">
                  {(selectedBooking.services_details || selectedBooking.services || []).map((svc, idx) => {
                    // Find the CORRECT staff for this specific service
                    const svcStaff = getStaffForService(svc.service_id);
                    const displayStaff = bookingStaff(selectedBooking, staff, calendars) || svcStaff;
                    return (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="font-medium text-indigo-900">{svc.name}</span>
                        <div className="flex items-center gap-2 text-indigo-600">
                          <span className="text-xs">{svc.duration_minutes || svc.duration} min</span>
                          {displayStaff && (
                            <div className="flex items-center gap-1">
                              {displayStaff.image_url ? (
                                <img src={displayStaff.image_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-indigo-200 flex items-center justify-center">
                                  <span className="text-[10px] font-bold text-indigo-600">{displayStaff.name?.charAt(0)}</span>
                                </div>
                              )}
                              <span className="text-xs font-medium">{displayStaff.name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Cliente *</Label>
                <Input 
                  value={editBookingForm.customer_name} 
                  onChange={(e) => setEditBookingForm({...editBookingForm, customer_name: e.target.value})} 
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={editBookingForm.customer_email} 
                  onChange={(e) => setEditBookingForm({...editBookingForm, customer_email: e.target.value})} 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input 
                value={editBookingForm.customer_phone} 
                onChange={(e) => setEditBookingForm({...editBookingForm, customer_phone: e.target.value})} 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data</Label>
                <Input 
                  type="date"
                  value={editBookingForm.start_datetime} 
                  onChange={(e) => setEditBookingForm({...editBookingForm, start_datetime: e.target.value})} 
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Ora</Label>
                <Input 
                  type="time"
                  value={editBookingForm.start_time} 
                  onChange={(e) => setEditBookingForm({...editBookingForm, start_time: e.target.value})} 
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Servizio</Label>
                <Select value={editBookingForm.service_id} onValueChange={(v) => setEditBookingForm({...editBookingForm, service_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.service_id} value={s.service_id}>
                        {s.name} ({s.duration_minutes} min)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Staff</Label>
                <Select value={editBookingForm.staff_id || "none"} onValueChange={(v) => setEditBookingForm({...editBookingForm, staff_id: v === "none" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona staff" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {staff.map((s) => (
                      <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Stato</Label>
              <Select value={editBookingForm.status} onValueChange={(v) => setEditBookingForm({...editBookingForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confermato</SelectItem>
                  <SelectItem value="pending">In attesa</SelectItem>
                  <SelectItem value="cancelled">Annullato</SelectItem>
                  <SelectItem value="completed">Completato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Service Duration Info */}
            {editBookingForm.service_id && (
              <div className="p-3 bg-slate-50 rounded-lg border">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Durata servizio:</span>
                  <span className="font-medium">
                    {services.find(s => s.service_id === editBookingForm.service_id)?.duration_minutes || 30} minuti
                  </span>
                </div>
                {editBookingForm.start_time && (
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-slate-600">Orario fine:</span>
                    <span className="font-medium">
                      {(() => {
                        const [h, m] = editBookingForm.start_time.split(':').map(Number);
                        const duration = services.find(s => s.service_id === editBookingForm.service_id)?.duration_minutes || 30;
                        const totalMin = h * 60 + m + duration;
                        return `${String(Math.floor(totalMin / 60) % 24).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
                      })()}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Note</Label>
              <Input 
                value={editBookingForm.notes} 
                onChange={(e) => setEditBookingForm({...editBookingForm, notes: e.target.value})} 
                placeholder="Note opzionali..."
              />
            </div>
            <DialogFooter className="flex justify-between">
              <Button 
                type="button" 
                variant="destructive" 
                onClick={() => selectedBooking && handleDeleteBooking(selectedBooking.booking_id)}
              >
                Elimina
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setEditBookingDialogOpen(false)}>Annulla</Button>
                <Button type="submit">Salva Modifiche</Button>
              </div>
            </DialogFooter>
          </form>
          
          {/* Right Column - Customer History */}
          <div className="border-l pl-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <History className="h-4 w-4" />
                Storico Cliente
              </h3>
              {customerHistory && !customerHistory.is_new_customer && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  {showHistory ? "Nascondi" : "Mostra"} prenotazioni
                </Button>
              )}
            </div>
            
            {loadingHistory && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
              </div>
            )}
            
            {!loadingHistory && customerHistory && (
              <div className="space-y-4">
                {/* Customer Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-primary">{customerHistory.total_bookings}</div>
                    <div className="text-xs text-slate-500">Prenotazioni totali</div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{customerHistory.completed_bookings}</div>
                    <div className="text-xs text-slate-500">Completate</div>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-amber-600">€{customerHistory.total_spent?.toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-slate-500">Spesa totale</div>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{customerHistory.cancelled_bookings}</div>
                    <div className="text-xs text-slate-500">Cancellate</div>
                  </div>
                </div>
                
                {/* Customer Info */}
                <div className="text-sm space-y-1 text-slate-600">
                  {customerHistory.first_booking_date && (
                    <p>Cliente dal: <span className="font-medium">{format(parseLocalDateTime(customerHistory.first_booking_date), "dd/MM/yyyy", { locale: it })}</span></p>
                  )}
                </div>
                
                {/* Past Bookings List */}
                {showHistory && customerHistory.bookings && customerHistory.bookings.length > 1 && (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    <h4 className="font-medium text-sm text-slate-700">Prenotazioni precedenti:</h4>
                    {customerHistory.bookings
                      .filter(b => b.booking_id !== selectedBooking?.booking_id)
                      .map((booking) => (
                        <div 
                          key={booking.booking_id}
                          className="p-3 bg-slate-50 rounded-lg border text-sm"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium">
                                {booking.start_datetime && format(parseLocalDateTime(booking.start_datetime), "dd/MM/yyyy HH:mm", { locale: it })}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                {booking.services_details?.map(s => s.name).join(", ") || booking.services?.map(s => s.name).join(", ")}
                              </div>
                            </div>
                            <Badge variant={
                              booking.status === 'completed' ? 'default' :
                              booking.status === 'confirmed' ? 'secondary' :
                              booking.status === 'cancelled' ? 'destructive' : 'outline'
                            }>
                              {booking.status === 'completed' ? 'Completato' :
                               booking.status === 'confirmed' ? 'Confermato' :
                               booking.status === 'cancelled' ? 'Annullato' : booking.status}
                            </Badge>
                          </div>
                          {booking.notes && (
                            <div className="mt-2 text-xs text-slate-500 italic">
                              Note: {booking.notes}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
                
                {customerHistory.is_new_customer && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                    <UserPlus className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm text-blue-700 font-medium">Prima prenotazione!</p>
                    <p className="text-xs text-blue-600 mt-1">Questo è un nuovo cliente</p>
                  </div>
                )}
              </div>
            )}
            
            {!loadingHistory && !customerHistory && editBookingForm.customer_email && (
              <div className="text-center py-8 text-slate-500">
                <p className="text-sm">Nessuno storico disponibile</p>
              </div>
            )}
            
            {!editBookingForm.customer_email && (
              <div className="text-center py-8 text-slate-500">
                <Mail className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Inserisci l'email del cliente per vedere lo storico</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Date Picker Dialog */}
    <Dialog open={datePickerOpen} onOpenChange={setDatePickerOpen}>
      <DialogContent className="sm:max-w-[350px] p-4">
        <DialogHeader>
          <DialogTitle>Seleziona Data</DialogTitle>
        </DialogHeader>
        <div className="flex justify-center">
          <CalendarPicker
            mode="single"
            selected={currentDate}
            onSelect={(date) => {
              if (date) {
                setCurrentDate(date);
                setDatePickerOpen(false);
              }
            }}
            locale={it}
            className="rounded-md border"
          />
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Add New Customer Dialog */}
    <Dialog open={showAddCustomerDialog} onOpenChange={setShowAddCustomerDialog}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-indigo-600" />
            Nuovo Cliente
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-slate-600 mb-4">
            Vuoi aggiungere <span className="font-semibold">{newCustomerData?.name}</span> alla lista clienti dell'azienda?
          </p>
          <p className="text-xs text-slate-500">
            In questo modo potrai visualizzare le statistiche e lo storico prenotazioni di questo cliente.
          </p>
          {newCustomerData && (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg border space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-slate-400" />
                <span>{newCustomerData.name}</span>
              </div>
              {newCustomerData.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <span>{newCustomerData.email}</span>
                </div>
              )}
              {newCustomerData.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-slate-400" />
                  <span>{newCustomerData.phone}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowAddCustomerDialog(false)}>
            No, grazie
          </Button>
          <Button onClick={handleAddNewCustomer} data-testid="add-customer-confirm">
            <UserPlus className="h-4 w-4 mr-2" />
            Aggiungi Cliente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    {/* Block Edit/Delete Dialog */}
    <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selectedBlock?.block_type === "day_off" ? (
              <>
                <CalendarX className="h-5 w-5 text-red-500" />
                Riposo Giornaliero
              </>
            ) : (
              <>
                <Coffee className="h-5 w-5 text-amber-500" />
                Pausa
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {selectedBlock && (
          <div className="space-y-4">
            {/* Block Details */}
            <div className={`p-4 rounded-lg ${selectedBlock.block_type === "day_off" ? "bg-red-50" : "bg-amber-50"}`}>
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-slate-500" />
                <span className="font-medium">
                  {staff.find(s => s.staff_id === selectedBlock.staff_id)?.name || "Staff"}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-slate-500" />
                <span>
                  {selectedBlock.start_datetime && format(parseLocalDateTime(selectedBlock.start_datetime), "EEEE d MMMM yyyy", { locale: it })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">
                  {selectedBlock.start_datetime && format(parseLocalDateTime(selectedBlock.start_datetime), "HH:mm")}
                  {" - "}
                  {selectedBlock.end_datetime && format(parseLocalDateTime(selectedBlock.end_datetime), "HH:mm")}
                </span>
              </div>
              {selectedBlock.notes && (
                <div className="mt-2 text-sm text-slate-600">
                  Note: {selectedBlock.notes}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex justify-between">
              <Button 
                variant="destructive" 
                onClick={handleDeleteBlock}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Elimina
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setBlockDialogOpen(false)}
              >
                Chiudi
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              Trascina il blocco nel calendario per spostarlo
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default CalendarView;
